import { useCallback, useState, useSyncExternalStore, type ReactNode } from 'react';

import { useTheme } from '../../hooks/useTheme';

import CommandPalette from './CommandPalette';
import ModeSwitch from './ModeSwitch';
import { getNavCollapsed, setNavCollapsed, subscribeNavCollapsed } from './navCollapse';
import NebariBottomBar from './NebariBottomBar';
import NebariMobileBar from './NebariMobileBar';
import NebariNavDrawer from './NebariNavDrawer';
import NebariNavPanel from './NebariNavPanel';
import NebariTopBar from './NebariTopBar';
import SearchTrigger from './SearchTrigger';
import type { AppModeInfo } from './use-app-mode';
import { useNavModel } from './use-nav-model';
import { useSearchPalette } from './useSearchPalette';

interface NebariNavProps {
  viewer: AppModeInfo;
  /** The routed page. The nav owns the scroll container around it so the
   *  bottom bar can sit outside it rather than floating over the content. */
  children: ReactNode;
}

/**
 * The global navigation, in every format at once.
 *
 * All four pieces render on every route and CSS decides which are visible —
 * `navtab:hidden` for the mobile pair, `hidden navtab:flex` for the top bar
 * and panel. Nothing here reads the viewport, so the first paint is already
 * correct and there is no resize listener to keep in sync. `display: none` also
 * takes the hidden format out of tab order and off the accessibility tree, so
 * the duplication is invisible to keyboard and screen-reader users.
 *
 * The search palette is the exception that has to be owned here: every format
 * has a trigger, they all open the same one, and ⌘K works from any of them.
 * The bars take their controls as slots, so this is the one place that decides
 * which controls a mode gets.
 *
 * The routed page renders throughout, including while the mode resolves —
 * only the chrome whose contents depend on it waits, so a slow
 * `fetchCurrentStaff` never blanks the page.
 */
export default function NebariNav({ viewer, children }: NebariNavProps) {
  const { theme } = useTheme();
  const nav = useNavModel(viewer);
  const collapse = useSyncExternalStore(subscribeNavCollapsed, getNavCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Teacher mode has no search. Loading reports teacher too, so search never
  // flashes up for a viewer who turns out to be a teacher.
  const searchEnabled = viewer.mode === 'admin';
  const { searchOpen, openSearch, closeSearch } = useSearchPalette(searchEnabled);
  // Stable, because the drawer keys an effect on it: a fresh closure each
  // render would re-run that effect and throw focus back to the drawer's
  // first row every time anything above re-rendered.
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  return (
    <>
      <NebariMobileBar
        drawerOpen={drawerOpen}
        onOpenDrawer={openDrawer}
        search={searchEnabled && <SearchTrigger variant="icon" onOpen={openSearch} />}
      />
      {/* The panel runs the full height beside the top bar, not under it —
          the brand mark sits at the top-left corner of the app. */}
      <div className="flex min-h-0 flex-1">
        {!viewer.loading && (
          <NebariNavPanel
            nav={nav}
            viewer={viewer}
            collapse={collapse}
            onSetCollapse={setNavCollapsed}
          />
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <NebariTopBar
            search={searchEnabled && <SearchTrigger variant="pill" onOpen={openSearch} />}
            actions={<ModeSwitch viewer={viewer} />}
          />
          <main className="min-h-0 flex-1 overflow-auto">{children}</main>
        </div>
      </div>
      {!viewer.loading && (
        <NebariBottomBar
          tabs={nav.bottomTabs}
          drawerOpen={drawerOpen}
          onOpenDrawer={nav.showMore ? openDrawer : undefined}
          onOpenSearch={searchEnabled ? openSearch : undefined}
        />
      )}
      {/* The drawer is where a phone reaches the account rows and Sign out,
          so teacher mode keeps it — with no sections in it. */}
      {drawerOpen && !viewer.loading && (
        <NebariNavDrawer nav={nav} viewer={viewer} onClose={closeDrawer} />
      )}
      {searchOpen && <CommandPalette theme={theme} onClose={closeSearch} />}
    </>
  );
}
